// /scada/events — events & alarms (R3). Real monitored alerts (alert_log) joined
// with operator responses (alert_responses). Three views: Active, Sequence-of-events
// (flat chronological), Archive (resolved/historical). Falls back to mock if API down.
import { useEffect, useMemo, useState } from 'react';
import ScadaLayout from '@/components/feature/ScadaLayout';
import { SectionCard } from '@/components/scada/kit';
import { scada } from '@/scada/tokens';
import { api } from '@/services/api';
import { mockEvents, type SysEvent, type Severity } from '@/scada/mock/events';

const SEV: Record<Severity, { c: string; icon: string; label: string }> = {
  alarm: { c: scada.state.alarm, icon: 'ri-alarm-warning-line', label: 'Аварія' },
  warning: { c: scada.state.warning, icon: 'ri-error-warning-line', label: 'Попередження' },
  info: { c: scada.state.curtailed, icon: 'ri-information-line', label: 'Подія' },
};
const fmt = (s: string) => new Date(s).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });

interface ApiEvent {
  id: string; ts: string; severity: Severity; stationName: string; message: string;
  status: 'active' | 'acknowledged' | 'cleared'; respBy: string | null;
  respLabel: string | null; respAt: string | null;
}

function Row({ e }: { e: SysEvent }) {
  const s = SEV[e.severity];
  return (
    <div className="flex items-center gap-3" style={{ padding: '8px 0', borderBottom: `1px solid ${scada.border}` }}>
      <i className={s.icon} style={{ color: s.c, fontSize: 16 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: scada.text, fontSize: 13 }}>
          {e.stationName}{e.device ? ` · ${e.device}` : ''} — {e.message}
        </div>
        <div style={{ color: scada.textMuted, fontSize: 11 }}>
          {fmt(e.ts)}
          {e.ackBy ? ` · відповів: ${e.ackBy}` : ''}
          {!e.ackBy && e.status === 'active' ? ' · очікує відповіді' : ''}
        </div>
      </div>
      {e.status === 'cleared' && <span style={{ color: scada.state.curtailed, fontSize: 11 }}>усунено</span>}
      {e.status === 'acknowledged' && <span style={{ color: scada.state.warning, fontSize: 11 }}>у роботі</span>}
    </div>
  );
}

export default function ScadaEventsPage() {
  const [events, setEvents] = useState<SysEvent[]>([]);
  const [view, setView] = useState<'active' | 'soe' | 'archive'>('active');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const data = await api.get<{ events: ApiEvent[] }>('/scada/events');
        if (!alive) return;
        const mapped: SysEvent[] = (data.events || []).map((e) => ({
          id: e.id,
          ts: e.ts,
          type: 'manual',
          severity: e.severity,
          stationId: '',
          stationName: e.stationName,
          message: e.message,
          status: e.status,
          ackBy: e.respBy ? `${e.respBy}${e.respLabel ? ` · ${e.respLabel}` : ''}` : undefined,
        }));
        setEvents(mapped);
      } catch {
        if (alive) setEvents(mockEvents());
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const active = useMemo(() => events.filter((e) => e.status === 'active' || e.status === 'acknowledged'), [events]);
  const archive = useMemo(() => events.filter((e) => e.status === 'cleared'), [events]);
  const soe = useMemo(() => [...events].sort((a, b) => +new Date(b.ts) - +new Date(a.ts)), [events]);

  const activeAlarmCount = active.filter((e) => e.severity === 'alarm' && e.status === 'active').length;

  const tab = (k: typeof view, label: string, n?: number) => {
    const on = view === k;
    return (
      <button onClick={() => setView(k)} style={{ background: on ? `${scada.accent}1A` : scada.panel2, color: on ? scada.text : scada.textDim, border: `1px solid ${on ? scada.accent + '60' : scada.border}`, borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>
        {label}{n != null ? ` (${n})` : ''}
      </button>
    );
  };

  return (
    <ScadaLayout active="events">
      <div className="flex items-center gap-2" style={{ marginBottom: 16 }}>
        {tab('active', 'Активні', active.length)}
        {tab('soe', 'Послідовність подій', soe.length)}
        {tab('archive', 'Архів', archive.length)}
      </div>

      {view === 'active' && (
        <SectionCard title="Активні події та аварії" subtitle={`${activeAlarmCount} відкритих аварій · джерело: моніторинг генерації`}>
          {active.map((e) => <Row key={e.id} e={e} />)}
          {!loading && active.length === 0 && <span style={{ color: scada.textMuted, fontSize: 13 }}>Активних подій немає.</span>}
          {loading && <span style={{ color: scada.textMuted, fontSize: 13 }}>Завантаження…</span>}
        </SectionCard>
      )}

      {view === 'soe' && (
        <SectionCard title="Послідовність подій (хронологія)" subtitle="Усі події за 14 днів у порядку часу">
          {soe.map((e) => <Row key={e.id} e={e} />)}
          {!loading && soe.length === 0 && <span style={{ color: scada.textMuted, fontSize: 13 }}>Подій немає.</span>}
        </SectionCard>
      )}

      {view === 'archive' && (
        <SectionCard title="Архів подій" subtitle="Усунені та історичні записи">
          {archive.map((e) => <Row key={e.id} e={e} />)}
          {!loading && archive.length === 0 && <span style={{ color: scada.textMuted, fontSize: 13 }}>Архів порожній.</span>}
        </SectionCard>
      )}
    </ScadaLayout>
  );
}
